import { useState, useEffect, useRef } from 'react';
import { User, Camera, Save, X, Loader2, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getAuthToken, getUserData, setAuthTokens } from '@/utils/auth';
import { apiUrl } from '@/api/config';
import { cn } from '@/lib/utils';
import { useTeacher } from '@/context/TeacherContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

export default function TeacherProfile() {
  const navigate = useNavigate();
  const { user: contextUser, teacherTheme } = useTeacher();
  const isDark = teacherTheme === 'dark';

  const [userProfile, setUserProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);

  const portal = 'teacher';

  // Load user profile data
  useEffect(() => {
    const loadProfile = () => {
      const userData = getUserData(portal);
      if (userData) {
        // Get profile photo from backend
        let photoUrl = null;
        const photoField = userData.profile_photo || userData.profile_pic_url || userData.photo || userData.avatar;

        if (photoField) {
          if (photoField.startsWith('http://') || photoField.startsWith('https://')) {
            photoUrl = photoField;
          } else {
            const cleanPath = photoField.startsWith('/') ? photoField.slice(1) : photoField;
            photoUrl = apiUrl(cleanPath);
          }
        }

        const profile = {
          photo: photoUrl,
          fullName: userData.full_name || `${userData.first_name || ''} ${userData.last_name || ''}`.trim() || userData.username || 'User',
          username: userData.username?.includes('__') ? userData.username.split('__')[1] : (userData.username || ''),
          schoolName: userData.school?.school_name || userData.school_name || null,
          email: userData.email || '',
          department: userData.department || 'Department',
          subject: userData.subject || 'Subject',
          userId: userData.id || userData.user_id || null
        };

        setUserProfile(profile);
        setFullName(profile.fullName);
        setProfilePhotoPreview(photoUrl);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [portal]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setFullName(userProfile?.fullName || '');
    setProfilePhoto(null);
    setProfilePhotoPreview(userProfile?.photo || null);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      toast.error('Full name cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      const token = getAuthToken(portal);
      if (!token) {
        throw new Error('Authentication required');
      }

      const formData = new FormData();
      formData.append('full_name', fullName.trim());
      if (profilePhoto) {
        formData.append('profile_photo', profilePhoto);
      }

      let response;
      try {
        response = await axios.patch(
          apiUrl('users/profile/'),
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } catch (patchError) {
        if (patchError.response?.status === 405 || patchError.response?.status === 404) {
          try {
            response = await axios.put(
              apiUrl('users/profile/'),
              formData,
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'multipart/form-data'
                }
              }
            );
          } catch (putError) {
            console.warn('Backend profile update endpoint not available');
            response = { data: { profile_photo: profilePhotoPreview, full_name: fullName.trim() } };
          }
        } else {
          throw patchError;
        }
      }

      try {
        const profileResponse = await axios.get(
          apiUrl('users/profile/'),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const updatedUserData = {
          ...getUserData(portal),
          ...profileResponse.data,
          full_name: fullName.trim(),
          first_name: fullName.trim().split(' ')[0] || '',
          last_name: fullName.trim().split(' ').slice(1).join(' ') || '',
          profile_photo: profileResponse.data?.profile_photo || profileResponse.data?.profile_pic_url || response.data?.profile_photo || response.data?.profile_pic_url
        };

        const refreshToken = localStorage.getItem(`${portal}_refresh_token`) || '';
        setAuthTokens(portal, token, refreshToken, updatedUserData);

        let updatedPhotoUrl = null;
        const photoField = updatedUserData.profile_photo || updatedUserData.profile_pic_url || updatedUserData.photo || updatedUserData.avatar;
        if (photoField) {
          if (photoField.startsWith('http://') || photoField.startsWith('https://')) {
            updatedPhotoUrl = photoField;
          } else {
            const cleanPath = photoField.startsWith('/') ? photoField.slice(1) : photoField;
            updatedPhotoUrl = apiUrl(cleanPath);
          }
        }

        setUserProfile({
          ...userProfile,
          fullName: fullName.trim(),
          photo: updatedPhotoUrl || profilePhotoPreview
        });
        setProfilePhotoPreview(updatedPhotoUrl || profilePhotoPreview);
      } catch (profileError) {
        const updatedUserData = {
          ...getUserData(portal),
          full_name: fullName.trim(),
          first_name: fullName.trim().split(' ')[0] || '',
          last_name: fullName.trim().split(' ').slice(1).join(' ') || '',
          profile_photo: response.data?.profile_photo || response.data?.profile_pic_url || userProfile?.photo
        };

        const refreshToken = localStorage.getItem(`${portal}_refresh_token`) || '';
        setAuthTokens(portal, token, refreshToken, updatedUserData);

        setUserProfile({
          ...userProfile,
          fullName: fullName.trim(),
          photo: response.data?.profile_photo || response.data?.profile_pic_url || profilePhotoPreview
        });
        setProfilePhotoPreview(response.data?.profile_photo || response.data?.profile_pic_url || profilePhotoPreview);
      }

      setProfilePhoto(null);
      setIsEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to update profile';
      toast.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className={cn(
        "flex items-center justify-center h-full",
        isDark ? "bg-[hsl(var(--main-bg))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]"
      )}>
        <Loader2 className={cn("w-8 h-8 animate-spin", isDark ? "text-[hsl(var(--green-bright))]" : "text-[#C9A961]")} />
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className={cn(
        "flex items-center justify-center h-full",
        isDark ? "bg-[hsl(var(--main-bg))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]"
      )}>
        <p className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]"}>No profile data available</p>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex flex-col h-full p-6 overflow-y-auto",
      isDark ? "bg-[hsl(var(--main-bg))]" : "bg-gradient-to-br from-[#F8F5F0] via-[#F5F1EA] to-[#F2EBE0]"
    )}>
      <div className="max-w-4xl mx-auto w-full space-y-6">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/teacher/dashboard')}
            className={cn(
              "p-2 rounded-lg transition-colors",
              isDark
                ? "hover:bg-[hsl(var(--sidebar-hover))]"
                : "hover:bg-white/60"
            )}
          >
            <ArrowLeft className={cn("w-5 h-5", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#6B6B6B]")} />
          </button>
          <div>
            <h1 className={cn("text-3xl font-bold", isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]")}>My Profile</h1>
            <p className={cn("mt-1", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]")}>Manage your profile information</p>
          </div>
        </div>

        {/* Profile Card */}
        <Card className={cn(
          "border-none shadow-sm",
          isDark
            ? "bg-[hsl(var(--card))]"
            : "bg-white"
        )}>
          <CardHeader className={cn(
            "pb-6",
            isDark ? "border-[hsl(var(--border))]" : "border-[#E8E1D6]"
          )}>
            <CardTitle className={isDark ? "text-[hsl(var(--foreground))]" : "text-[#2B2B2B]"}>Profile Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Profile Photo Section */}
            <div className="flex flex-col items-center space-y-4">
              <div className="relative">
                <div className={cn(
                  "w-32 h-32 rounded-full flex items-center justify-center overflow-hidden",
                  isDark
                    ? "bg-gradient-to-r from-[hsl(var(--gold-primary))] to-[hsl(var(--gold-secondary))]"
                    : "bg-gradient-to-r from-[#C9A961] to-[#D4AF77]"
                )}>
                  {profilePhotoPreview ? (
                    <img
                      src={profilePhotoPreview}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={cn(
                    "w-full h-full items-center justify-center",
                    profilePhotoPreview ? "hidden" : "flex"
                  )}>
                    <User className="w-16 h-16 text-white" />
                  </div>
                </div>
                {isEditing && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      "absolute bottom-0 right-0 p-2 rounded-full transition-colors shadow-lg",
                      isDark
                        ? "bg-[hsl(var(--green-bright))] hover:bg-[hsl(var(--teal-vivid))]"
                        : "bg-[#C9A961] hover:bg-[#D4AF77]"
                    )}
                    title="Change photo"
                  >
                    <Camera className="w-5 h-5 text-white" />
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </div>
              {isEditing && (
                <p className={cn("text-xs text-center", isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]")}>
                  Click the camera icon to change your profile photo
                </p>
              )}
            </div>

            {/* Profile Information */}
            <div className="space-y-4">
              {/* Profile Fields Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Full Name - Editable */}
                <div className="text-left">
                  <Label htmlFor="fullName" className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]"}>Full Name</Label>
                  {isEditing ? (
                    <Input
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className={cn(
                        "mt-2",
                        isDark
                          ? "bg-[hsl(var(--main-bg))] border-[hsl(var(--border))] text-white"
                          : "bg-[#FAF8F5] border-[#E8E1D6] text-[#2B2B2B]"
                      )}
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <p className={cn("mt-2 font-medium", isDark ? "text-white" : "text-[#2B2B2B]")}>{userProfile.fullName}</p>
                  )}
                </div>

                {/* Username - Read-only */}
                <div className="text-left">
                  <Label className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]"}>Username</Label>
                  <p className={cn("mt-2", isDark ? "text-white" : "text-[#8B7B6B]")}>{userProfile.username}</p>
                </div>

                {/* School - Read-only */}
                {userProfile.schoolName && (
                  <div className="text-left">
                    <Label className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]"}>School</Label>
                    <p className={cn("mt-2", isDark ? "text-white" : "text-[#8B7B6B]")}>{userProfile.schoolName}</p>
                  </div>
                )}

                {/* Email - Read-only */}
                {userProfile.email && (
                  <div className="text-left">
                    <Label className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]"}>Email</Label>
                    <p className={cn("mt-2", isDark ? "text-white" : "text-[#8B7B6B]")}>{userProfile.email}</p>
                  </div>
                )}

                {/* Department - Read-only */}
                {userProfile.department && (
                  <div className="text-left">
                    <Label className={isDark ? "text-[hsl(var(--muted-foreground))]" : "text-[#8B7B6B]"}>Department</Label>
                    <p className={cn("mt-2", isDark ? "text-white" : "text-[#8B7B6B]")}>{userProfile.department}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center sm:justify-start gap-3 pt-4">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className={cn(
                      "text-white transition-colors",
                      isDark
                        ? "bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]"
                        : "bg-gradient-to-r from-[#C9A961] to-[#D4AF77] hover:from-[#D4AF77] hover:to-[#C9A961]"
                    )}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    variant="outline"
                    className={cn(
                      isDark
                        ? "border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--sidebar-hover))] hover:text-white"
                        : "border-[#E8E1D6] text-[#2B2B2B] hover:bg-white/60"
                    )}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleEditClick}
                  className={cn(
                    "text-white transition-colors",
                    isDark
                      ? "bg-gradient-to-r from-[hsl(var(--green-bright))] to-[hsl(var(--teal-vivid))]"
                      : "bg-gradient-to-r from-[#C9A961] to-[#D4AF77] hover:from-[#D4AF77] hover:to-[#C9A961]"
                  )}
                >
                  <User className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
